import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import apiClient from '@/services/api';
import type { SalesAreaReport as SalesAreaReportData } from '@/types/api';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Sales Area: sold items grouped by account sales area, matching the POS Sales Area report. */
export default function SalesAreaReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  // Report-local controls (kept off the shared ReportFiltersBar so only this report is affected).
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  // Time window is only sent when both bounds are set; blank = whole day (default behaviour).
  const hasWindow = !!(startTime && endTime);

  const { data, isLoading, isError, refetch } = useQuery<SalesAreaReportData>({
    queryKey: ['sales-area', selectedRestaurantId, startDate, endDate, startTime, endTime],
    queryFn: () =>
      apiClient.getSalesAreaBreakdown(
        selectedRestaurantId!,
        startDate,
        endDate,
        hasWindow ? startTime : undefined,
        hasWindow ? endTime : undefined
      ),
    enabled: !!selectedRestaurantId,
  });

  const areas = data?.areas ?? [];
  // The single-area filter is applied client-side against the already-grouped result.
  const shown = selectedArea ? areas.filter((a) => a.name === selectedArea) : areas;

  const shownItems = shown.reduce((n, a) => n + a.items.length, 0);
  const shownRevenue = shown.reduce((s, a) => s + Number(a.revenue), 0);

  const titleSuffix = `${selectedArea ? ` — ${selectedArea}` : ''}${hasWindow ? ` (${startTime}–${endTime})` : ''}`;

  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const sections = (): ReportSection[] =>
    shown.map((a) => ({
      heading: `${a.name} — ${Number(a.quantity).toLocaleString()} items · ${formatCurrency(a.revenue)}`,
      headers: ['Item', 'Price', 'Qty', 'Total'],
      rows: a.items.map((it) => [
        it.name,
        formatCurrency(it.price),
        Number(it.quantity).toLocaleString(),
        formatCurrency(it.revenue),
      ]),
    }));
  const kpis = (): [string, string][] => {
    const k: [string, string][] = [
      ['Sales Areas', shown.length.toLocaleString()],
      ['Items', shownItems.toLocaleString()],
      ['Revenue', formatCurrency(shownRevenue)],
    ];
    if (hasWindow) k.push(['Time window', `${startTime}–${endTime}`]);
    return k;
  };

  const inputClass =
    'w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500';

  return (
    <div className="space-y-6">
      {/* Report-specific controls: daily time-of-day window (restaurant-local) + single-area filter. */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="sa-start-time" className="block text-sm font-medium text-gray-700 mb-1">
                Start time
              </label>
              <input
                id="sa-start-time"
                type="time"
                value={startTime}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartTime(v);
                  if (v && endTime && v > endTime) setEndTime(v);
                }}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="sa-end-time" className="block text-sm font-medium text-gray-700 mb-1">
                End time
              </label>
              <input
                id="sa-end-time"
                type="time"
                value={endTime}
                onChange={(e) => {
                  const v = e.target.value;
                  setEndTime(v);
                  if (v && startTime && v < startTime) setStartTime(v);
                }}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="sa-area" className="block text-sm font-medium text-gray-700 mb-1">
                Sales area
              </label>
              <select
                id="sa-area"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
                className={inputClass}
              >
                <option value="">All areas</option>
                {areas.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {(startTime || endTime) && !hasWindow && (
            <p className="mt-2 text-xs text-gray-500">Set both start and end time to apply the window.</p>
          )}
        </div>
      </div>

      <ReportFrame
        title="Sales Area"
        description="Sold items grouped by account sales area. Each item's area is resolved from its own sales-area tag, falling back to the area its menu group is linked to — matching the POS Sales Area report. The optional time window uses the restaurant's local clock."
        hasRestaurant={!!selectedRestaurantId}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => refetch()}
        isEmpty={shown.length === 0}
        exportDisabled={!data}
        onExportCsv={() =>
          data &&
          downloadCsv(
            `sales-area-${format(new Date(), 'yyyy-MM-dd')}.csv`,
            buildGenericCsv(`Sales Area${titleSuffix}`, meta(), kpis(), sections())
          )
        }
        onExportPdf={() => data && printGenericReport(`Sales Area Report${titleSuffix}`, meta(), kpis(), sections())}
      >
        {shown.map((a) => (
          <div className="card" key={a.name}>
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{a.name}</h3>
                <span className="text-sm text-gray-500">
                  {Number(a.quantity).toLocaleString()} items · {formatCurrency(a.revenue)}
                </span>
              </div>
              <SimpleTable
                headers={['Item', 'Price', 'Qty', 'Total']}
                rows={a.items.map((it) => [
                  it.name,
                  formatCurrency(it.price),
                  Number(it.quantity).toLocaleString(),
                  formatCurrency(it.revenue),
                ])}
              />
            </div>
          </div>
        ))}
      </ReportFrame>
    </div>
  );
}
